import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';

export interface DictionaryDeleteDialogProps {
  open: boolean;
  title: string;
  description: string;
  /** Shown instead of the generic message when the backend answers 409 RESOURCE_IN_USE. */
  inUseMessage: string;
  pending: boolean;
  error: unknown;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
}

/** Confirms a dictionary delete and stays open with the refusal when the item is still in use. */
export function DictionaryDeleteDialog({
  open,
  title,
  description,
  inUseMessage,
  pending,
  error,
  onConfirm,
  onOpenChange,
}: DictionaryDeleteDialogProps) {
  const { t } = useTranslation(['dictionary', 'common', 'errors']);
  const message = useErrorMessage();
  const requestId = isApiError(error) ? error.requestId : undefined;
  const inUse = isApiError(error) && error.code === 'RESOURCE_IN_USE';

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {error ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>
              {inUse ? inUseMessage : message(error)}
              {requestId ? (
                <span className="block text-xs">{t('errors:requestIdShort', { requestId })}</span>
              ) : null}
            </AlertDescription>
          </Alert>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>{t('common:actions.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            disabled={pending}
            onClick={(event) => {
              // Radix closes on click; stay open until the backend has answered.
              event.preventDefault();
              onConfirm();
            }}
          >
            {pending ? t('dictionary:delete.submitting') : t('dictionary:delete.submit')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
